import {DOCUMENT} from '@angular/common';
import {Inject, Injectable} from '@angular/core';

/**
 * Injects the Google Tag Manager container snippet into the page.
 *
 * The container id is environment-specific and supplied by the server through
 * `/app/pre-init-info` (populated per-environment via Ansible: a VCR container
 * for production, a VCR-nonprod container for QA/beta). When no id is provided
 * (e.g. local dev) analytics stays disabled.
 *
 * Once GTM loads, GA4 automatically records browser, OS, screen resolution and
 * the initial pageview.
 */
@Injectable({providedIn: 'root'})
export class GtmService {

  // Guard so the snippet is only ever injected once per page load.
  private initialized = false;

  constructor(@Inject(DOCUMENT) private document: Document) {}

  public init(containerId: string | undefined | null): void {
    if (this.initialized) {
      return;
    }
    if (!containerId) {
      // eslint-disable-next-line no-console
      console.debug('GTM disabled (no container id for this environment)');
      return;
    }
    this.initialized = true;

    // <script> in <head> - bootstraps dataLayer and async-loads gtm.js
    const script = this.document.createElement('script');
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${containerId}');
    `;
    this.document.head.appendChild(script);

    // <noscript> iframe fallback in <body>
    const noscript = this.document.createElement('noscript');
    const iframe = this.document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.cssText = 'display:none;visibility:hidden';
    noscript.appendChild(iframe);
    this.document.body.prepend(noscript);

    // eslint-disable-next-line no-console
    console.debug('GTM/GA enabled:', containerId);
  }
}
